/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import { EditableText } from '../Editable.js';
import { inputStyle, monoLabel } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';

const p = appPalette;

const HORIZON_LABEL: Record<'life' | 'year' | 'quarter', string> = {
  life: 'LIFE',
  year: 'YEAR',
  quarter: 'QUARTER',
};

export function Goals() {
  const utils = trpc.useUtils();
  const list = trpc.goals.list.useQuery();
  const create = trpc.goals.create.useMutation({
    onSuccess: async () => {
      await utils.goals.list.invalidate();
    },
  });
  const achieve = trpc.goals.achieve.useMutation({
    onSuccess: async () => {
      await utils.goals.list.invalidate();
    },
  });
  const drop = trpc.goals.drop.useMutation({
    onSuccess: async () => {
      await utils.goals.list.invalidate();
    },
  });
  const update = trpc.goals.update.useMutation({
    onSuccess: async () => {
      await utils.goals.list.invalidate();
    },
  });

  const [draftHorizon, setDraftHorizon] = useState<'life' | 'year' | 'quarter'>('quarter');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const title = draftTitle.trim();
    if (!title) return;
    const description = draftDescription.trim();
    create.mutate(
      { horizon: draftHorizon, title, description: description === '' ? null : description },
      {
        onSuccess: () => {
          setDraftTitle('');
          setDraftDescription('');
          setDraftHorizon('quarter');
        },
      },
    );
  };

  const items = list.data ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ ...monoLabel }}>Goals · life / year / quarter</div>

      {list.isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : items.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 15,
            color: p.muted,
            margin: 0,
          }}
        >
          No goals on file.
        </p>
      ) : (
        <div>
          {items.map((g) => (
            <GoalRow
              key={g.id}
              horizon={g.horizon}
              title={g.title}
              description={g.description}
              onAchieve={() => achieve.mutate({ goalId: g.id })}
              onDrop={() => drop.mutate({ goalId: g.id })}
              onEditTitle={(next) => update.mutate({ goalId: g.id, title: next })}
              onEditDescription={(next) =>
                update.mutate({ goalId: g.id, description: next === '' ? null : next })
              }
              onEditHorizon={(next) => update.mutate({ goalId: g.id, horizon: next })}
              pending={achieve.isPending || drop.isPending || update.isPending}
            />
          ))}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select
            value={draftHorizon}
            onChange={(e) => setDraftHorizon(e.target.value as 'life' | 'year' | 'quarter')}
            aria-label="Goal horizon"
            style={{ ...inputStyle, width: 120, fontFamily: 'var(--font-mono)', fontSize: 11 }}
          >
            <option value="life">LIFE</option>
            <option value="year">YEAR</option>
            <option value="quarter">QUARTER</option>
          </select>
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="Goal title"
            aria-label="Goal title"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="submit"
            disabled={create.isPending || draftTitle.trim() === ''}
            style={{
              padding: '10px 18px',
              background: p.fg,
              color: p.bg,
              border: 'none',
              fontSize: 13,
              cursor: create.isPending || draftTitle.trim() === '' ? 'not-allowed' : 'pointer',
              opacity: create.isPending || draftTitle.trim() === '' ? 0.6 : 1,
            }}
          >
            {create.isPending ? 'Adding…' : 'Add →'}
          </button>
        </div>
        <input
          type="text"
          value={draftDescription}
          onChange={(e) => setDraftDescription(e.target.value)}
          placeholder="Description (optional) — what does success look like?"
          aria-label="Goal description"
          style={{ ...inputStyle, fontStyle: 'italic' }}
        />
      </form>
      {create.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{create.error.message}</span>
      ) : null}
    </section>
  );
}

function GoalRow({
  horizon,
  title,
  description,
  onAchieve,
  onDrop,
  onEditTitle,
  onEditDescription,
  onEditHorizon,
  pending,
}: {
  horizon: 'life' | 'year' | 'quarter';
  title: string;
  description: string | null;
  onAchieve: () => void;
  onDrop: () => void;
  onEditTitle: (next: string) => void;
  onEditDescription: (next: string) => void;
  onEditHorizon: (next: 'life' | 'year' | 'quarter') => void;
  pending: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto auto 1fr auto',
        alignItems: 'start',
        columnGap: 12,
        rowGap: 4,
        padding: '12px 0',
        borderTop: `1px solid ${p.line}`,
      }}
    >
      <button
        type="button"
        onClick={onAchieve}
        disabled={pending}
        title="Mark achieved"
        aria-label="Mark achieved"
        style={{
          width: 18,
          height: 18,
          border: `1px solid ${p.line}`,
          background: 'transparent',
          cursor: pending ? 'wait' : 'pointer',
          marginTop: 2,
        }}
      />
      <select
        value={horizon}
        disabled={pending}
        onChange={(e) => onEditHorizon(e.target.value as 'life' | 'year' | 'quarter')}
        aria-label="Edit horizon"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.18em',
          cursor: pending ? 'wait' : 'pointer',
          padding: 0,
          marginTop: 3,
        }}
      >
        <option value="life">{HORIZON_LABEL.life}</option>
        <option value="year">{HORIZON_LABEL.year}</option>
        <option value="quarter">{HORIZON_LABEL.quarter}</option>
      </select>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <EditableText
          value={title}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === '' || trimmed === title) return;
            onEditTitle(trimmed);
          }}
          ariaLabel="Edit goal title"
          renderStyle={{ fontSize: 15, color: p.fg, fontWeight: 500 }}
          inputStyle={{ ...inputStyle, fontSize: 15, padding: '6px 8px' }}
        />
        <EditableText
          value={description ?? ''}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === (description ?? '')) return;
            onEditDescription(trimmed);
          }}
          ariaLabel="Edit goal description"
          renderStyle={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 14,
            color: p.muted,
          }}
          inputStyle={{ ...inputStyle, fontSize: 14, padding: '6px 8px', fontStyle: 'italic' }}
          placeholder="Add a description"
        />
      </div>
      <button
        type="button"
        onClick={onDrop}
        disabled={pending}
        title="Drop"
        aria-label="Drop"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: pending ? 'wait' : 'pointer',
          fontSize: 16,
          padding: '0 4px',
          marginTop: 2,
        }}
      >
        ×
      </button>
    </div>
  );
}
